import { onboardingApi } from "@features/onboarding/utility-setup/api";

import {
  authApiClient,
  cxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";

import {
  ActivateConsumerPayload,
  ActivateConsumerResponse,
  CreateConsumerNotePayload,
  CreateConsumerNoteResponse,
  OnboardingSummary,
  UpdateConsumerApprovalPayload,
  UpdateConsumerApprovalResponse,
  UpdateConsumerDocumentPayload,
  UpdateConsumerDocumentResponse,
  UpdateConsumerInstallationPayload,
  UpdateConsumerInstallationResponse,
  UpdateConsumerServicePayload,
  UpdateConsumerServiceResponse,
  UpdateConsumerStatusResponse,
  UpdateMeterInstallationPayload,
  UpdateMeterInstallationResponse,
} from "./types";
import { ApiEndpoints } from "@shared/api/api-endpoints";

export const CXCxOnboardingApi = {
  getSummary: async (params: {
    remoteUtilityId: any;
    activeTab: string;
  }): Promise<OnboardingSummary> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-summary",
      (qs) => {
        qs.push("remote_utility_id", params.remoteUtilityId);
        qs.push("active_tab", params.activeTab);
      }
    );
    const response = await cxApiClient.get<{ result: OnboardingSummary }>(url);
    return response.data.result;
  },

  getConsumerList: async (params: {
    remoteUtilityId: any;
    page: number;
    limit: number;
    searchData: string;
    status: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-add",
      (qs) => {
        qs.push("remote_utility_id", params.remoteUtilityId);
        if (params.page) qs.push("page", params.page);
        if (params.limit) qs.push("limit", params.limit);
        if (params.searchData) qs.push("search_data", params.searchData);
        if (params.status) qs.push("status", params.status);
      }
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  exportConsumerList: async (params: {
    remoteUtilityId: any;
    page?: number;
    limit?: number;
    searchData?: string;
    status?: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-add",
      (qs) => {
        qs.push("remote_utility_id", params.remoteUtilityId);
        if (params.page) qs.push("page", params.page);
        if (params.limit) qs.push("limit", params.limit);
        if (params.searchData) qs.push("search_data", params.searchData);
        if (params.status) qs.push("status", params.status);
        qs.push("export_format", "csv");
      }
    );

    const response = await cxApiClient.get(url);

    return response.data;
  },

  getConsumerListHistory: async (params: {
    remoteUtilityId: any;
    page: number;
    limit: number;
    searchData: string;
    status: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-add",
      (qs) => {
        qs.push("remote_utility_id", params.remoteUtilityId);
        qs.push("is_history", "1");
        if (params.page) qs.push("page", params.page);
        if (params.limit) qs.push("limit", params.limit);
        if (params.searchData) qs.push("search_data", params.searchData);
        if (params.status) qs.push("status", params.status);
      }
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  exportConsumerListHistory: async (params: {
    remoteUtilityId: any;
    page?: number;
    limit?: number;
    searchData?: string;
    status?: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-add",
      (qs) => {
        qs.push("remote_utility_id", params.remoteUtilityId);
        qs.push("is_history", "1");
        if (params.page) qs.push("page", params.page);
        if (params.limit) qs.push("limit", params.limit);
        if (params.searchData) qs.push("search_data", params.searchData);
        if (params.status) qs.push("status", params.status);
        qs.push("export_format", "csv");
      }
    );

    const response = await cxApiClient.get(url);

    return response.data;
  },

  getDetails: async (params: {
    consumer_id: string;
    remote_utility_id: any;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-detail",
      (qs) => {
        qs.push("consumer_id", params.consumer_id);
        qs.push("remote_utility_id", params.remote_utility_id);
      }
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  getDocument: async (params: {
    consumer_id: string;
    remote_utility_id: any;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-document",
      (qs) => {
        qs.push("consumer_id", params.consumer_id);
        qs.push("remote_utility_id", params.remote_utility_id);
      }
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  updateConsumerDocument: async (
    document_id: any,
    payload: UpdateConsumerDocumentPayload
  ): Promise<UpdateConsumerDocumentResponse> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "consumer-document",
      [document_id]
    );
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  updateConsumerStatus: async (
    consumerId: string,
    status: string
  ): Promise<UpdateConsumerStatusResponse> => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "consumer", [
      consumerId,
      status,
    ]);
    const response = await cxApiClient.put(url);
    return response.data;
  },

  getServiceOrder: async (params: {
    remote_utility_id: any;
    search_data: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "search-service-order",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("search_data", params.search_data);
      }
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  updateConsumerService: async (
    consumerId: string,
    payload: UpdateMeterInstallationPayload
  ): Promise<UpdateMeterInstallationResponse> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "consumer_status",
      [consumerId, "SURVEY_INPROGRESS"]
    );
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  updateMeterInstallation: async (
    consumerId: string,
    payload: UpdateConsumerServicePayload
  ): Promise<UpdateConsumerServiceResponse> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "consumer_status",
      [consumerId, "INSTALLATION_INPROGRESS"]
    );
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  updateConsumerApprovalStatus: async (
    consumerId: string,
    payload: UpdateConsumerApprovalPayload
  ): Promise<UpdateConsumerApprovalResponse> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "consumer_status",
      [consumerId, "APPROVED"]
    );
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  getServiceOrderByConsumer: async (params: {
    remote_utility_id: any;
    consumer_id: string;
    tab_name: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "service-order-by-consumer",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("consumer_id", params.consumer_id);
        qs.push("tab_name", params.tab_name);
      }
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  createConsumerNote: async (
    payload: CreateConsumerNotePayload
  ): Promise<CreateConsumerNoteResponse> => {
    const url = ApiEndpoints.createUrl("cx", "consumer-note");
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  getNotes: async (params: {
    remote_utility_id: any;
    request_id: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-request/notes",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("request_id", params.request_id);
        qs.push("source", "consumer");
      }
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  downloadReport: async (params: {
    remote_utility_id: any;
    report_id: string;
  }): Promise<Blob> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `download-report/${params.report_id}`,
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
      }
    );
    const response = await cxApiClient.get(url, {
      responseType: "blob",
    });
    return response.data;
  },

searchMeter: async (params: {
  remote_utility_id: string;
  utility_service: string;
  premise: string;
  device_number?: string;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "search-meter",
    (qs) => {
      qs.push("remote_utility_id", params.remote_utility_id);
      qs.push("utility_service", params.utility_service);
      qs.push("premise", params.premise);

      // Only include device_number if defined and not empty
      if (params.device_number) {
        qs.push("device_number", params.device_number);
      }
    }
  );

  const response = await cxApiClient.get(url);
  return response.data;
},


  activateConsumer: async (
    consumerId: string,
    payload: ActivateConsumerPayload
  ): Promise<ActivateConsumerResponse> => {
    const response = await cxApiClient.put(
      `consumer_status/${consumerId}/ACTIVATE/`,
      payload
    );
    return response.data;
  },

  getStatus: async (): Promise<any> => {
    const response = await cxApiClient.get("choices/consumer_status");
    return response.data;
  },
  updateConsumerInstallationStatus: async (
    consumerId: string,
    payload: any
  ): Promise<any> => {
    const response = await cxApiClient.put(
      `consumer_status/${consumerId}/INSTALLATION_COMPLETED/`,
      payload
    );
    return response.data;
  },

  skipSiteInspection: async (consumerId: string): Promise<any> => {
    const response = await cxApiClient.put(
      `consumer_status/${consumerId}/APPROVAL_INPROGRESS/`
    );
    return response.data;
  },

  // GET API for service order by consumer
  getServiceOrderConsumer: async (params: {
    remote_utility_id: string;
    consumer_id: string;
    tab_name: string;
  }): Promise<any> => {
    const response = await cxApiClient.get(
      `service-order-by-consumer/?remote_utility_id=${
        params.remote_utility_id
      }&consumer_id=${params.consumer_id}&tab_name=${encodeURIComponent(
        params.tab_name
      )}`
    );
    return response.data;
  },

  getConsumerMeter: async (params: {
    consumer_id: string;
    remote_utility_id: string;
  }): Promise<any> => {
    const response = await cxApiClient.get(
      `consumer-meter/?consumer_id=${params.consumer_id}&remote_utility_id=${params.remote_utility_id}`
    );
    return response.data;
  },

  updateStatus: async (consumerId: number, payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "nsc-progress-status-change",
      [consumerId.toString()]
    );
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },
};
